import React, { useEffect, useState } from 'react';
import './LandingApp.css';
import { api, urlEstudio } from '../utils/api';
import { LogoLockup, BankIcon, LockIcon, WhatsAppIcon, WhatsAppIconSmall } from '../components/Icons';
import ClaveInput from '../components/ClaveInput';
import { CICLOS, CHATBULARIS, PLANES_PANEL, MAX_ABOGADOS_PUBLICO, planPara, cobroTotal, clp, type Ciclo, type Producto } from '../utils/tarifa';
import { CONSULTA_PENDIENTE } from './EstadoPublico';
import { registrarVisita } from '../utils/visitas';
import CarruselCapsulas from './CarruselCapsulas';

// Página pública de Tabularis (dominio raíz). Presenta el servicio y ofrece dos
// accesos: registrar un estudio nuevo o ingresar al panel de un estudio.
export const COLOR = { navy: '#0D1B35', blue: '#1A3A6E', blue2: '#2756A8', line: '#DDE6F3', muted: '#6B82A0', wa: '#25D366' };

// La franja de precios va en dorado, el tono de la esquina del logo: llama la
// atención sobre el azul sin parecer liquidación. El dorado claro es para
// fondos oscuros; el oscuro, para texto sobre blanco.
const ORO = '#E3B54F';
const ORO_TXT = '#8A6110';

/** Franja sobre la cabecera: anuncia los precios nuevos (nov-2026) en toda la
 *  página sin tocar el mensaje del hero (14 días gratis, sin tarjeta). */
const FranjaPrecios: React.FC = () => (
  <a href="#planes" className="lp-franja" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, flexWrap: 'wrap', background: COLOR.navy, color: '#fff', textDecoration: 'none', padding: '9px 16px', fontSize: 13.5, lineHeight: 1.4, textAlign: 'center' }}>
    <span style={{ color: ORO, fontWeight: 800, letterSpacing: '.08em', fontSize: 11.5, textTransform: 'uppercase' }}>Nuevos precios</span>
    <span className="lp-franja-txt">
      Desde <strong>{clp(PLANES_PANEL[0].precio)}</strong> al mes
      <span className="lp-franja-largo"> · hasta {PLANES_PANEL[1].abogados} abogados por {clp(PLANES_PANEL[1].precio)}</span>
    </span>
    <span className="lp-franja-ver" style={{ color: ORO, fontWeight: 600 }}>Ver precio →</span>
  </a>
);

// Dónde encontrarnos. Está en un solo lugar porque aparece en varios: el
// botón flotante, el bloque de contacto, el pie y la sección de quiénes somos.
// Un número escrito cuatro veces a mano es un número que un día queda distinto
// en una de las cuatro.
export const CONTACTO = {
  whatsapp: '+56 9 6786 9656',
  whatsappUrl: 'https://wa.me/56967869656?text=' + encodeURIComponent('Hola, quiero saber más de Tabularis.'),
  email: 'contacto@tabularis.cl',
  direccion: "O'Higgins 940, oficina 410",
  ciudad: 'Concepción, Chile',
};

// Todo lo que incluye, sin tramos: la única variable es cuántos abogados son.
const INCLUYE = [
  'Gestión de casos, procesos y etapas',
  'Pagos, cuotas y cobranza por caso',
  'Documentos (archivos y foto→PDF)',
  'Flujo de caja con reparto configurable',
  'Reportes por período contable propio',
  'Captadores ilimitados, con bonos y ranking',
  'Dos procuradores incluidos por cada abogado',
  'Redactor IA de evaluaciones',
  'Disciplinas y plantillas propias',
  'Datos en Chile, protegidos por la Ley 21.719',
];

// Las letras con tilde (y la ñ) pasan a su letra sin tilde: "Soto & Ríos"
// sugiere "sotoriosabogados", no "sotorosabogados".
const sanitizarSub = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9-]/g, '').replace(/^-+|-+$/g, '');

// Precios. El panel se cobra por plan según cuántos abogados son; Chatbularis IA es
// prepago: se carga saldo y cada consulta descuenta lo que cuesta. Todo
// estudio tiene Chatbularis; lo que se elige es si además lleva el panel.
const PRODUCTOS: { id: Producto; etiqueta: string; detalle: string }[] = [
  { id: 'tabularis', etiqueta: 'Panel + Chatbularis', detalle: 'Gestión del estudio y redacción con IA' },
  { id: 'chatbularis', etiqueta: 'Solo Chatbularis', detalle: 'Redacción con IA, sin mensualidad' },
];
const esPanel = (p: Producto) => p !== 'chatbularis';

const Planes: React.FC<{ onRegistro: (abogados: number, producto: Producto) => void }> = ({ onRegistro }) => {
  // Parte en uno: es el precio de entrada, y el que llega solo tiene que ver
  // SU número sin tocar nada. Quien es más, elige su plan.
  const [abogados, setAbogados] = useState<number>(PLANES_PANEL[0].abogados);
  const [ciclo, setCiclo] = useState<Ciclo>('mensual');
  const [producto, setProducto] = useState<Producto>('tabularis');
  const c = cobroTotal(abogados, ciclo, producto);
  const conPanel = esPanel(producto);

  return (
    <section id="planes" style={{ padding: '3vw 6vw 4vw', maxWidth: 1160, margin: '0 auto' }}>
      <h2 style={{ textAlign: 'center', fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.6rem, 3vw, 2.4rem)', color: COLOR.navy }}>Precio</h2>
      <p style={{ textAlign: 'center', color: COLOR.muted, marginTop: 6 }}>
        El panel, según cuántos abogados son; Chatbularis, por lo que usas · CLP con IVA · Sin tarjeta para empezar
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24, marginTop: 30, alignItems: 'start' }}>
        {/* Calculadora */}
        <div className="lp-tarjeta" style={{ border: `1.5px solid ${conPanel ? ORO : COLOR.blue2}`, borderRadius: 16, padding: '26px 24px', background: COLOR.navy, color: '#fff', boxShadow: conPanel ? '0 12px 34px rgba(227,181,79,0.22)' : '0 12px 30px rgba(13,27,53,0.18)', position: 'relative' }}>
          {conPanel && (
            <div style={{ position: 'absolute', top: -13, left: 20, background: ORO, color: COLOR.navy, fontSize: 11.5, fontWeight: 800, letterSpacing: '.07em', textTransform: 'uppercase', borderRadius: 999, padding: '5px 12px', boxShadow: '0 4px 12px rgba(13,27,53,0.25)' }}>
              Nuevos precios
            </div>
          )}
          <div style={{ fontSize: 13.5, color: '#B9C6DE' }}>¿Qué necesitas?</div>
          <div style={{ display: 'flex', gap: 6, margin: '8px 0 16px', flexWrap: 'wrap' }}>
            {PRODUCTOS.map((pr) => (
              <button key={pr.id} onClick={() => setProducto(pr.id)} style={{
                flex: '1 1 40%', padding: '8px 6px', borderRadius: 10, fontSize: 12.5, cursor: 'pointer', lineHeight: 1.25,
                border: `1px solid ${producto === pr.id ? '#fff' : 'rgba(255,255,255,0.28)'}`,
                background: producto === pr.id ? '#fff' : 'transparent',
                color: producto === pr.id ? COLOR.navy : '#fff', fontWeight: producto === pr.id ? 700 : 400,
              }}>
                {pr.etiqueta}
                <div style={{ fontSize: 10.5, fontWeight: 400, opacity: 0.8 }}>{pr.detalle}</div>
              </button>
            ))}
          </div>

          {conPanel && (
            <>
              <div style={{ fontSize: 13.5, color: '#B9C6DE' }}>¿Cuántos abogados son?</div>
              <div style={{ display: 'flex', gap: 6, margin: '8px 0 6px', flexWrap: 'wrap' }}>
                {PLANES_PANEL.map((pl) => (
                  <button key={pl.abogados} onClick={() => setAbogados(pl.abogados)} style={{
                    flex: '1 1 28%', padding: '8px 6px', borderRadius: 10, fontSize: 12.5, cursor: 'pointer', lineHeight: 1.25,
                    border: `1px solid ${abogados === pl.abogados ? '#fff' : 'rgba(255,255,255,0.28)'}`,
                    background: abogados === pl.abogados ? '#fff' : 'transparent',
                    color: abogados === pl.abogados ? COLOR.navy : '#fff', fontWeight: abogados === pl.abogados ? 700 : 400,
                  }}>
                    {pl.etiqueta}
                    <div style={{ fontSize: 10.5, fontWeight: 400, opacity: 0.8 }}>{pl.procuradores} procuradores</div>
                  </button>
                ))}
              </div>
              <div style={{ fontSize: 12.5, color: '#B9C6DE' }}>
                Cuenta solo a los abogados. Los captadores no se cobran. ¿Más de {MAX_ABOGADOS_PUBLICO}? Escríbenos y lo conversamos.
              </div>
            </>
          )}

          {conPanel ? (<>
          <div style={{ display: 'flex', gap: 8, margin: '20px 0 16px' }}>
            {(Object.keys(CICLOS) as Ciclo[]).map((k) => (
              <button key={k} onClick={() => setCiclo(k)} style={{
                flex: 1, padding: '9px 6px', borderRadius: 10, fontSize: 13, cursor: 'pointer',
                border: `1px solid ${ciclo === k ? '#fff' : 'rgba(255,255,255,0.28)'}`,
                background: ciclo === k ? '#fff' : 'transparent',
                color: ciclo === k ? COLOR.navy : '#fff', fontWeight: ciclo === k ? 700 : 400,
              }}>
                {CICLOS[k].etiqueta}
                {CICLOS[k].nota && <div style={{ fontSize: 10.5, fontWeight: 400, opacity: 0.8 }}>{CICLOS[k].nota}</div>}
              </button>
            ))}
          </div>

          <div style={{ fontSize: 38, fontWeight: 700, lineHeight: 1.1 }}>
            {clp(c.mensualEquivalenteClp)}<span style={{ fontSize: 15, fontWeight: 400, color: '#B9C6DE' }}> /mes</span>
          </div>
          <div style={{ fontSize: 13.5, color: '#B9C6DE', marginTop: 6, lineHeight: 1.55 }}>
            {ciclo === 'mensual'
              ? <>Pagas mes a mes, sin permanencia. Pagando anual queda en <strong style={{ color: ORO }}>{clp(cobroTotal(abogados, 'anual', producto).mensualEquivalenteClp)} al mes</strong>.</>
              : <>Se paga {clp(c.totalClp)} cada {c.meses} meses · ahorras <strong style={{ color: '#8FE0B0' }}>{clp(c.ahorroClp)}</strong>.</>}
            {' '}Chatbularis va aparte, prepago: pagas solo lo que usas.
          </div>
          </>) : (
            /* Solo Chatbularis: no hay mensualidad. Lo que se muestra es lo
               que cuesta usarlo, que es lo que el abogado necesita estimar. */
            <div>
              <div style={{ fontSize: 38, fontWeight: 700, lineHeight: 1.1 }}>
                $0<span style={{ fontSize: 15, fontWeight: 400, color: '#B9C6DE' }}> /mes</span>
              </div>
              <div style={{ fontSize: 13.5, color: '#B9C6DE', marginTop: 6, lineHeight: 1.55 }}>
                Partes con {clp(CHATBULARIS.bienvenidaClp)} de regalo y cargas desde {clp(CHATBULARIS.cargas[0].montoClp)} cuando quieras.
                Una consulta para redactar cuesta ≈ {clp(CHATBULARIS.redactar.tipicoClp)}, y conversar, menos.
              </div>
            </div>
          )}
          {/* Lleva lo elegido al registro: es lo mismo que fija los cupos y el
              plan del estudio, así que no hay que preguntarlo dos veces. */}
          <button onClick={() => onRegistro(abogados, producto)} style={{ ...btnPrimary, background: '#fff', color: COLOR.navy, width: '100%', marginTop: 20 }}>
            {conPanel
              ? `Empezar la prueba de 14 días: ${planPara(abogados).etiqueta.toLowerCase()}`
              : `Empezar con ${clp(CHATBULARIS.bienvenidaClp)} de regalo`}
          </button>
          <div style={{ fontSize: 12, color: '#B9C6DE', marginTop: 10, textAlign: 'center' }}>
            Sin tarjeta para empezar, con {clp(CHATBULARIS.bienvenidaClp)} de saldo de Chatbularis de regalo. Después pagas con {conPanel ? 'débito, crédito o transferencia' : 'débito o crédito'}.
          </div>
          <MediosDePagoFila />
        </div>

        {/* Qué incluye */}
        <div style={{ display: 'grid', gap: 24 }}>
          <div className="lp-tarjeta" style={{ border: `1.5px solid ${COLOR.line}`, borderRadius: 16, padding: '26px 24px', background: '#fff' }}>
            <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 20, fontWeight: 600, color: COLOR.navy }}>El panel, todo incluido</div>
            <div style={{ fontSize: 13, color: COLOR.muted, marginTop: 2 }}>No hay funciones reservadas para el plan caro.</div>
            <ul style={{ listStyle: 'none', padding: 0, margin: '16px 0 0', fontSize: 14, lineHeight: 1.95, color: COLOR.navy }}>
              {INCLUYE.map((f) => <li key={f}>✓ {f}</li>)}
            </ul>
          </div>

          {/* Chatbularis es prepago: se muestra lo que cuesta una consulta,
              que es lo que el abogado necesita para decidir. */}
          <div className="lp-tarjeta" style={{ border: `1.5px solid ${COLOR.line}`, borderRadius: 16, padding: '26px 24px', background: '#fff' }}>
            <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: COLOR.muted }}>Prepago · sin mensualidad</div>
            <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 20, fontWeight: 600, color: COLOR.navy, margin: '4px 0 6px' }}>Chatbularis IA</div>
            <p style={{ fontSize: 14, lineHeight: 1.6, color: COLOR.muted, margin: 0 }}>
              Escribe demandas y escritos completos conversando, apoyado en jurisprudencia de la Corte Suprema y las Cortes de Apelaciones (más de 1.100 fallos), el texto vigente de las leyes desde la BCN y búsquedas en sitios oficiales del Estado.
              Adjuntas expedientes y fotos, y responde sobre ellos. Con el panel o solo. <a href="#chatbularis" style={{ color: COLOR.blue2 }}>Ver cómo redacta ↑</a>
            </p>
            {/* Conversar y Redactar adelante: son el uso de todos los días.
                Análisis profundo va en una línea: con su precio al lado de los
                otros, el número que se recordaba era el más alto (oct-2026).
                Conversar aún no tiene precio medido (el modo se registra
                desde el 7-oct); cuando lo tenga, va su "≈" acá. */}
            <div className="lp-modos" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 14 }}>
              <div style={{ border: `1.5px solid ${COLOR.blue2}`, borderRadius: 12, padding: '12px 14px', background: '#F5F8FD' }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: COLOR.navy }}>Conversar</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: COLOR.blue2, margin: '3px 0 2px' }}>El más económico</div>
                <div style={{ fontSize: 12, color: COLOR.muted, lineHeight: 1.45 }}>Dudas rápidas, como a un colega: qué plazo aplica, si algo procede. Responde breve y cuesta menos que redactar.</div>
              </div>
              <div style={{ border: `1px solid ${COLOR.line}`, borderRadius: 12, padding: '12px 14px' }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: COLOR.navy }}>Redactar</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: COLOR.navy, fontVariantNumeric: 'tabular-nums' }}>≈ {clp(CHATBULARIS.redactar.tipicoClp)}<span style={{ fontSize: 11.5, fontWeight: 400, color: COLOR.muted }}> por consulta</span></div>
                <div style={{ fontSize: 12, color: COLOR.muted, lineHeight: 1.45, marginTop: 2 }}>Demandas y escritos completos, y revisiones. Las largas, hasta ≈ {clp(CHATBULARIS.redactar.hastaClp)}.</div>
              </div>
            </div>
            <p style={{ fontSize: 12.5, lineHeight: 1.55, color: COLOR.muted, margin: '10px 0 0' }}>
              Para casos complejos (estrategia, nulidad, casación) está el modo <strong style={{ color: COLOR.navy }}>Análisis profundo</strong>, que razona más y cuesta más. Lo eliges solo cuando lo necesitas.
            </p>
            <p style={{ fontSize: 12.5, lineHeight: 1.6, color: COLOR.muted, margin: '12px 0 0' }}>
              Todo estudio parte con <strong style={{ color: COLOR.navy }}>{clp(CHATBULARIS.bienvenidaClp)} de saldo de regalo</strong>. Cargas desde {clp(CHATBULARIS.cargas[0].montoClp)} y queda disponible apenas pagas; lo cargado no vence. Bajo cada respuesta ves cuánto costó.
            </p>
          </div>
        </div>
      </div>

      <p style={{ textAlign: 'center', color: COLOR.muted, marginTop: 22, fontSize: 13.5, lineHeight: 1.6 }}>
        El panel: {PLANES_PANEL.map((pl, i) => <React.Fragment key={pl.abogados}>{i > 0 && (i === PLANES_PANEL.length - 1 ? ' y ' : ', ')}{pl.etiqueta.toLowerCase()} con {pl.procuradores} procuradores, <strong style={{ color: COLOR.navy }}>{clp(pl.precio)}</strong></React.Fragment>)} al mes.
        Más de {MAX_ABOGADOS_PUBLICO} abogados, lo conversamos. Cambias de plan cuando quieras.
      </p>
      {/* Quién cobra. En Chile es lo primero que se mira para saber si hay una
          empresa real detrás, y es dato público: sale en cada factura. */}
      <p style={{ textAlign: 'center', color: COLOR.muted, marginTop: 6, fontSize: 12.5 }}>
        Cobra <strong style={{ color: COLOR.navy }}>Tabularis SpA</strong> · RUT 78.486.429-4 · {CONTACTO.direccion}, {CONTACTO.ciudad}
      </p>
    </section>
  );
};

// Cómo se paga. Va pegado al precio y no en una caja aparte más abajo, porque
// la duda "¿y cómo cobran?" aparece en el instante en que se mira el número.
//
// Manda el logo del banco y no el texto. Un logo conocido se reconoce de un
// vistazo y responde "¿quién me va a cobrar?" sin que nadie tenga que leer un
// párrafo — y el que desconfía no está leyendo, está buscando una señal.
const medioCol: React.CSSProperties = { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, textAlign: 'center' };
const medioIcono: React.CSSProperties = { height: 24, display: 'flex', alignItems: 'center', color: COLOR.blue2 };
const medioTxt: React.CSSProperties = { fontSize: 11.5, fontWeight: 600, color: COLOR.navy, lineHeight: 1.3 };

export const MediosDePagoFila: React.FC = () => (
  <div style={{ marginTop: 14, background: '#fff', borderRadius: 12, padding: '12px 10px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
    <div style={medioCol}>
      <div style={medioIcono}>
        <img src="/banchile-pagos.png" alt="Banchile Pagos, el portal de pagos del Banco de Chile" style={{ width: 96, height: 'auto', display: 'block' }} />
      </div>
      <div style={medioTxt}>Débito o crédito</div>
    </div>
    <div style={medioCol}>
      <div style={medioIcono}><BankIcon size={22} /></div>
      <div style={medioTxt}>Transferencia</div>
    </div>
    <div style={medioCol}>
      <div style={medioIcono}><LockIcon size={20} strokeWidth={1.6} /></div>
      <div style={medioTxt}>Tu tarjeta no pasa por Tabularis</div>
    </div>
  </div>
);

// Así funciona. Tres pasos que dicen lo mismo que el hero pero en orden de
// tiempo: primero pruebas, después decides, y recién ahí pagas. Va antes de
// las funciones porque la objeción "¿y si me cobran?" frena más que cualquier
// función que falte.
const PASOS: [string, string, React.ReactNode][] = [
  ['1', 'Crea tu estudio en un minuto', 'Nombre del estudio, tu correo y una contraseña. Sin tarjeta, sin instalar nada y sin hablar con un vendedor.'],
  ['2', 'Úsalo 14 días con tus casos reales', <>Con tu equipo, tus causas y tus documentos. Todo el panel, sin funciones bloqueadas; y {clp(CHATBULARIS.bienvenidaClp)} de saldo de Chatbularis de regalo.</>],
  ['3', 'Si te convence, pagas. Si no, no pasa nada', 'Pagas con débito, crédito o transferencia, desde el panel. No hay cobros automáticos: si no sigues, no pagas nada y tus datos quedan guardados hasta 6 meses para que los descargues.'],
];

const ComoFunciona: React.FC<{ onRegistro: () => void }> = ({ onRegistro }) => (
  <section id="como-funciona" style={{ padding: '3vw 6vw 2vw', maxWidth: 1120, margin: '0 auto' }}>
    <div style={{ textAlign: 'center', marginBottom: 26 }}>
      <div style={{ fontSize: 12.5, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLOR.blue2 }}>Así funciona</div>
      <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.5rem, 3vw, 2.2rem)', color: COLOR.navy, margin: '8px 0 0' }}>Primero lo pruebas. Después decides.</h2>
    </div>
    <div className="lp-pasos" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 18 }}>
      {PASOS.map(([n, t, d]) => (
        <div key={n} className="lp-ficha" style={{ border: `1px solid ${COLOR.line}`, borderRadius: 16, padding: '24px 22px', background: '#fff', position: 'relative' }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: COLOR.navy, color: '#fff', fontWeight: 700, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{n}</div>
          <div style={{ fontWeight: 600, fontSize: 17, color: COLOR.navy, margin: '14px 0 6px', lineHeight: 1.3 }}>{t}</div>
          <div style={{ color: COLOR.muted, fontSize: 14.5, lineHeight: 1.6 }}>{d}</div>
        </div>
      ))}
    </div>
    <div style={{ textAlign: 'center', marginTop: 22 }}>
      <button onClick={onRegistro} style={{ ...btnPrimary, padding: '13px 26px', fontSize: 15.5 }}>Probar 14 días gratis →</button>
      <div style={{ fontSize: 12.5, color: COLOR.muted, marginTop: 8 }}>Sin tarjeta · Sin instalar · Cancela cuando quieras</div>
    </div>
  </section>
);

// Dos herramientas, una cuenta. Va bajo el hero porque es la primera duda de
// quien llega: "¿esto es un gestor o una IA?". Es las dos cosas, y se pueden
// contratar por separado; dicho en dos tarjetas, el visitante elige camino en
// cinco segundos en vez de descubrirlo en la calculadora de precio.
const tarjetaHerr: React.CSSProperties = { border: `1px solid ${COLOR.line}`, borderRadius: 16, padding: '24px 22px', background: '#fff', display: 'flex', flexDirection: 'column', gap: 10 };

export const DosHerramientas: React.FC<{ onRegistro: (producto: Producto) => void }> = ({ onRegistro }) => (
  <section style={{ padding: '2vw 6vw 1vw', maxWidth: 1120, margin: '0 auto' }}>
    <div style={{ textAlign: 'center', marginBottom: 20 }}>
      <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.5rem, 3vw, 2.2rem)', color: COLOR.navy, margin: '0 0 8px' }}>Dos herramientas, una cuenta</h2>
      <p style={{ color: COLOR.muted, fontSize: 15.5, margin: 0, lineHeight: 1.6 }}>El panel ordena tu estudio. Chatbularis redacta contigo. Los contratas juntos o por separado.</p>
    </div>
    <div className="lp-dos" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: 18 }}>
      <div className="lp-ficha" style={tarjetaHerr}>
        <div style={{ fontSize: 26 }}>🗂️</div>
        <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 21, fontWeight: 600, color: COLOR.navy }}>Panel de causas</div>
        <p style={{ color: COLOR.muted, fontSize: 14.5, lineHeight: 1.6, margin: 0, flex: 1 }}>
          Casos, plazos, audiencias, pagos y equipo en un solo lugar, y tus clientes consultan su caso solos.
        </p>
        <div style={{ fontSize: 13, color: COLOR.muted, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span>Desde <strong style={{ color: COLOR.navy, fontSize: 15 }}>{clp(PLANES_PANEL[0].precio)}</strong> al mes</span>
        </div>
        <div style={{ fontSize: 12, color: ORO_TXT, fontWeight: 600, marginTop: -4 }}>Hasta {PLANES_PANEL[1].abogados} abogados por {clp(PLANES_PANEL[1].precio)} · hasta {PLANES_PANEL[2].abogados} por {clp(PLANES_PANEL[2].precio)}</div>
        <div className="lp-herr-btns" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
          <button onClick={() => onRegistro('tabularis')} style={btnPrimary}>Probar 14 días gratis</button>
          <a href="#beneficios" style={{ ...btnGhost, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>Ver qué incluye ↓</a>
        </div>
      </div>
      <div className="lp-ficha" style={{ ...tarjetaHerr, borderColor: COLOR.blue2, background: '#F5F8FD' }}>
        <div style={{ fontSize: 26 }}>✨</div>
        <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 21, fontWeight: 600, color: COLOR.navy }}>Chatbularis IA</div>
        <p style={{ color: COLOR.muted, fontSize: 14.5, lineHeight: 1.6, margin: 0, flex: 1 }}>
          Demandas y escritos completos conversando, con jurisprudencia de la Corte Suprema y las Cortes de Apelaciones, las leyes vigentes de la BCN y búsqueda en sitios oficiales del Estado.
        </p>
        <div style={{ fontSize: 13, color: COLOR.muted }}>Prepago, sin mensualidad · una consulta para redactar ≈ <strong style={{ color: COLOR.navy }}>{clp(CHATBULARIS.redactar.tipicoClp)}</strong></div>
        <div className="lp-herr-btns" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
          <button onClick={() => onRegistro('chatbularis')} style={btnPrimary}>Probar Chatbularis gratis</button>
          <a href="#chatbularis" style={{ ...btnGhost, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>Ver cómo redacta ↓</a>
        </div>
      </div>
    </div>
  </section>
);

// Una conversación de muestra, dibujada en HTML y no capturada: no hay video
// del asistente todavía, y una captura real llevaría datos de un caso. El
// texto es inventado y lo dice el pie.
export const ChatMock: React.FC = () => (
  <div className="lp-chat-mock" style={{ background: '#0A1529', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14, padding: 16, display: 'grid', gap: 12, fontSize: 13.5, lineHeight: 1.55 }}>
    <div style={{ justifySelf: 'end', maxWidth: '88%', background: COLOR.blue2, color: '#fff', borderRadius: '14px 14px 4px 14px', padding: '10px 14px' }}>
      Trabajador despedido sin aviso previo tras 4 años en la empresa, sin finiquito. Necesito la demanda por despido injustificado para el Juzgado de Letras del Trabajo de Concepción.
      <div style={{ fontSize: 11, opacity: 0.75, marginTop: 6 }}>📎 contrato.pdf · carta-despido.jpg</div>
    </div>
    <div style={{ maxWidth: '92%', background: 'rgba(255,255,255,0.07)', color: '#E6EDF7', borderRadius: '14px 14px 14px 4px', padding: '12px 14px', fontFamily: 'Georgia, serif' }}>
      <div style={{ fontWeight: 700, letterSpacing: '0.02em' }}>EN LO PRINCIPAL: demanda por despido injustificado y cobro de prestaciones; PRIMER OTROSÍ: acompaña documentos; SEGUNDO OTROSÍ: patrocinio y poder.</div>
      <div style={{ marginTop: 8, opacity: 0.9 }}>S. J. L. del Trabajo de Concepción</div>
      <div style={{ marginTop: 6, opacity: 0.9 }}>… en representación de don …, trabajador, domiciliado en …, a US. respetuosamente digo: que vengo en interponer demanda en procedimiento de aplicación general, conforme a los artículos 168, 162 y 163 del Código del Trabajo, en contra de …<span style={{ display: 'inline-block', width: 8, height: 15, background: '#8FB0E6', marginLeft: 3, verticalAlign: 'text-bottom' }} /></div>
      <div style={{ fontSize: 11, color: '#8FB0E6', marginTop: 10, fontFamily: 'Inter, sans-serif' }}>Razonamiento profundo · Opus 5 · 3 fuentes de la base jurídica</div>
    </div>
    <div style={{ fontSize: 11, color: '#6B82A0', textAlign: 'center' }}>Conversación de muestra, con datos inventados.</div>
  </div>
);

// Chatbularis con sección propia. Hasta ahora solo existía dentro del precio,
// así que quien no llegaba hasta ahí no sabía que Tabularis redacta. Va en
// azul marino para que se distinga del panel de un vistazo.
export const ChatbularisSeccion: React.FC<{ onRegistro: () => void }> = ({ onRegistro }) => (
  <section id="chatbularis" style={{ padding: '2vw 6vw 3vw', maxWidth: 1120, margin: '0 auto' }}>
    <div className="lp-chat-caja" style={{ background: COLOR.navy, color: '#fff', borderRadius: 18, padding: 'clamp(24px, 4vw, 44px)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 32, alignItems: 'center', boxShadow: '0 16px 40px rgba(13,27,53,0.22)' }}>
      <div>
        <div style={{ fontSize: 12.5, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8FB0E6' }}>Chatbularis IA</div>
        <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.5rem, 3.2vw, 2.2rem)', margin: '8px 0 12px', lineHeight: 1.15 }}>Redacta demandas y escritos conversando</h2>
        <p style={{ color: '#C3D2EA', fontSize: 15, lineHeight: 1.6, margin: 0 }}>
          Le cuentas el caso como se lo contarías a un colega y te devuelve el escrito completo, listo para revisar. Con el panel o solo.
        </p>
        <ul style={{ listStyle: 'none', padding: 0, margin: '16px 0 0', color: '#E6EDF7', fontSize: 14.5, lineHeight: 1.7, display: 'grid', gap: 6 }}>
          <li>✓ Jurisprudencia de la Corte Suprema y las Cortes de Apelaciones: más de 1.100 fallos en laboral, familia, civil y penal</li>
          <li>✓ El texto vigente de códigos y leyes, tomado de la BCN y revisado cada día</li>
          <li>✓ Busca en sitios oficiales: Poder Judicial, BCN, Dirección del Trabajo, Contraloría, SII y Tribunal Constitucional</li>
          <li>✓ Adjuntas expedientes, contratos y fotos, y responde sobre ellos</li>
          <li>✓ <strong>Redactar</strong> para escritos completos y revisiones, <strong>Conversar</strong> para dudas rápidas y <strong>Análisis profundo</strong> para los casos difíciles</li>
          <li>✓ Lo descargas en Word o PDF y lo terminas a tu manera</li>
        </ul>
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', marginTop: 22 }}>
          <button onClick={onRegistro} style={{ ...btnPrimary, background: '#fff', color: COLOR.navy, padding: '13px 24px', fontSize: 15.5 }}>Probar Chatbularis gratis →</button>
          <span style={{ fontSize: 13, color: '#B9C6DE' }}>{clp(CHATBULARIS.bienvenidaClp)} de regalo · sin tarjeta</span>
        </div>
        <div style={{ fontSize: 13, color: '#8FB0E6', marginTop: 12 }}>
          Sin mensualidad: pagas lo que usas, ≈ {clp(CHATBULARIS.redactar.tipicoClp)} por consulta. <a href="#planes" style={{ color: '#fff' }}>Ver precios</a>
        </div>
      </div>
      <ChatMock />
    </div>
  </section>
);

// Preguntas frecuentes. Son las que llegan por WhatsApp antes de registrarse;
// contestarlas acá ahorra el mensaje y, sobre todo, la duda. Nada de lo que
// dice va más allá de lo que el sistema hace: gracia de 7 días en solo lectura,
// datos guardados hasta 6 meses, nada se borra solo.
const PREGUNTAS: [string, React.ReactNode][] = [
  ['¿Necesito tarjeta para probar?', 'No. Registras el estudio con su nombre, tu correo y una contraseña, y tienes 14 días completos. No pedimos tarjeta ni ningún dato de pago.'],
  ['¿Qué pasa cuando terminan los 14 días?', 'Nada automático. Si quieres seguir, pagas desde el panel. Si no, el estudio queda en modo lectura: puedes consultar y descargar lo tuyo, y tus datos se guardan hasta 6 meses. Nunca los borramos solos.'],
  ['¿Cómo se paga?', <>Con débito o crédito a través de Banchile Pagos, el portal de pagos del Banco de Chile, o por transferencia. Tu tarjeta no pasa por Tabularis. Cobra Tabularis SpA y recibes tu factura.</>],
  ['¿Y si somos más de 5 abogados?', `Escríbenos por WhatsApp o a ${CONTACTO.email} y armamos un plan para tu estudio. Hasta ${MAX_ABOGADOS_PUBLICO} abogados eliges el plan tú mismo, y lo cambias cuando quieras desde Configuración.`],
  ['¿Puedo cancelar?', 'Sí, cuando quieras, desde el panel. No hay contrato de permanencia: el panel se paga mes a mes y cambias de plan en cualquier momento. Chatbularis no tiene mensualidad: es prepago.'],
  ['¿Dónde quedan mis datos?', 'En servidores de AWS en Sudamérica, cifrados y aislados por estudio. Tabularis SpA está constituida en Chile, así que tus datos quedan protegidos por la Ley 21.719. Los descargas cuando quieras.'],
  ['¿Sirve para mi área del derecho?', 'Sí. Familia, laboral, civil, penal, cobranza y las demás: cada disciplina trae sus etapas y plantillas, y puedes crear las tuyas.'],
];

export const Faq: React.FC<{ preguntas?: [string, React.ReactNode][] }> = ({ preguntas = PREGUNTAS }) => (
  <section id="preguntas" style={{ padding: '2vw 6vw 3vw', maxWidth: 820, margin: '0 auto' }}>
    <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.5rem, 3vw, 2.2rem)', color: COLOR.navy, margin: '0 0 22px', textAlign: 'center' }}>Preguntas frecuentes</h2>
    <div style={{ display: 'grid', gap: 10 }}>
      {preguntas.map(([q, a]) => (
        <details key={q} className="lp-faq" style={{ border: `1px solid ${COLOR.line}`, borderRadius: 12, background: '#F8FAFD', padding: '0 20px' }}>
          <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 15.5, color: COLOR.navy, padding: '15px 0', listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            {q}<span className="lp-faq-mas" aria-hidden="true">+</span>
          </summary>
          <div style={{ color: COLOR.muted, fontSize: 14.5, lineHeight: 1.65, padding: '0 0 16px' }}>{a}</div>
        </details>
      ))}
    </div>
  </section>
);

// El panel en marcha. Un bucle mudo de 38 segundos al lado del mensaje: quien
// llega ve el producto antes de leer una sola función. Los archivos llevan
// versión en el nombre porque el bucket los cachea para siempre; para cambiar
// el video se sube otro con -v2 y se apunta acá.
const DEMO = { bucle: '/hero-panel-v2.mp4', portada: '/hero-panel-v2.jpg', completo: '/demo-panel-v3.mp4', duracion: '1 min' };

const DemoVideo: React.FC<{ onVer: () => void }> = ({ onVer }) => {
  // Con "reducir movimiento" activado en el sistema, el bucle no arranca solo:
  // queda la portada y el botón para verlo.
  const [animar, setAnimar] = useState(true);
  useEffect(() => {
    try { if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) setAnimar(false); } catch { /* navegador sin matchMedia */ }
  }, []);
  // React no siempre deja el atributo muted en el DOM, y sin él Safari y
  // Chrome se niegan a reproducir solos: se fija a mano en el nodo.
  const listo = (el: HTMLVideoElement | null) => {
    if (!el || !animar) return;
    el.muted = true; el.defaultMuted = true;
    el.play().catch(() => { /* el navegador lo bloqueó: queda la portada */ });
  };
  return (
    <div className="lp-demo" style={{ borderRadius: 14, overflow: 'hidden', background: COLOR.navy, boxShadow: '0 24px 60px rgba(13,27,53,0.28)', border: '1px solid rgba(13,27,53,0.12)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 12px', background: '#152647' }}>
        {['#FF5F57', '#FEBC2E', '#28C840'].map((c) => <span key={c} style={{ width: 10, height: 10, borderRadius: '50%', background: c, display: 'block' }} />)}
        <span style={{ marginLeft: 8, flex: 1, height: 18, borderRadius: 5, background: 'rgba(255,255,255,0.08)', fontSize: 10.5, color: '#8FB0E6', lineHeight: '18px', paddingLeft: 8 }}>tuestudio.tabularis.cl</span>
      </div>
      <button type="button" onClick={onVer} aria-label={`Ver la demo con voz, ${DEMO.duracion}`} style={{ display: 'block', width: '100%', padding: 0, border: 'none', background: 'none', cursor: 'pointer', position: 'relative' }}>
        <video ref={listo} src={DEMO.bucle} poster={DEMO.portada} autoPlay={animar} muted loop playsInline preload="metadata"
          style={{ display: 'block', width: '100%', aspectRatio: '16 / 9', objectFit: 'cover' }} />
        <span style={{ position: 'absolute', right: 12, bottom: 12, display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(13,27,53,0.85)', color: '#fff', borderRadius: 999, padding: '8px 14px', fontSize: 13, fontWeight: 600 }}>
          ▶ Ver con voz · {DEMO.duracion}
        </span>
      </button>
    </div>
  );
};

// Ventana con el video completo, con voz. Se carga recién al abrirla: son
// 10 MB que no tiene por qué bajar quien no la pide.
const VideoModal: React.FC<{ onClose: () => void; onEmpezar: () => void }> = ({ onClose, onEmpezar }) => {
  useEffect(() => {
    const previo = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    return () => { document.body.style.overflow = previo; window.removeEventListener('keydown', esc); };
  }, [onClose]);
  return (
    <div onClick={onClose} role="dialog" aria-modal="true" aria-label="Demo de Tabularis" style={{ position: 'fixed', inset: 0, background: 'rgba(13,27,53,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 960 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#fff', marginBottom: 10 }}>
          <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 19 }}>Así se ve Tabularis</div>
          <button type="button" aria-label="Cerrar" onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', padding: '4px 6px', lineHeight: 1 }}>✕</button>
        </div>
        <video src={DEMO.completo} controls autoPlay playsInline style={{ display: 'block', width: '100%', aspectRatio: '16 / 9', borderRadius: 12, background: '#000' }} />
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', alignItems: 'center', marginTop: 16, flexWrap: 'wrap' }}>
          <button onClick={onEmpezar} style={{ ...btnPrimary, background: '#fff', color: COLOR.navy, padding: '12px 24px', fontSize: 15 }}>Probar 14 días gratis →</button>
          <span style={{ color: '#B9C6DE', fontSize: 13 }}>Sin tarjeta · Sin instalar</span>
        </div>
      </div>
    </div>
  );
};

// Formulario de consultas. Escribe a la casilla del negocio; el visitante
// nunca elige el destinatario. El campo "web" es una trampa para robots: está
// oculto, una persona jamás lo completa.
const Contacto: React.FC = () => {
  const [f, setF] = useState({ nombre: '', email: '', estudio: '', telefono: '', mensaje: '', web: '' });
  const [estado, setEstado] = useState<'idle' | 'enviando' | 'ok'>('idle');
  const [error, setError] = useState('');
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setF(v => ({ ...v, [k]: e.target.value }));

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setEstado('enviando');
    try {
      await api('/contacto', { method: 'POST', body: f, auth: false });
      setEstado('ok');
    } catch (err: any) {
      setError(err?.message || 'No pudimos enviar tu consulta. Intenta de nuevo.');
      setEstado('idle');
    }
  };

  if (estado === 'ok') return (
    <div style={{ background: '#F8FAFD', border: `1px solid ${COLOR.line}`, borderRadius: 14, padding: '32px 28px', textAlign: 'center' }}>
      <div style={{ fontSize: 30, marginBottom: 10 }}>✉️</div>
      <div style={{ fontWeight: 600, fontSize: 17, marginBottom: 6 }}>Recibimos tu consulta</div>
      <div style={{ color: COLOR.muted, fontSize: 14.5 }}>Te respondemos a {f.email} dentro del día hábil.</div>
    </div>
  );

  return (
    <form onSubmit={enviar} style={{ background: '#F8FAFD', border: `1px solid ${COLOR.line}`, borderRadius: 14, padding: '26px 24px', display: 'grid', gap: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
        <input style={input} placeholder="Tu nombre" value={f.nombre} onChange={set('nombre')} required />
        <input style={input} type="email" placeholder="Tu correo" value={f.email} onChange={set('email')} required />
        <input style={input} placeholder="Estudio (opcional)" value={f.estudio} onChange={set('estudio')} />
        <input style={input} placeholder="Teléfono (opcional)" value={f.telefono} onChange={set('telefono')} />
      </div>
      <textarea style={{ ...input, minHeight: 120, resize: 'vertical', fontFamily: 'inherit' }}
        placeholder="¿En qué te podemos ayudar?" value={f.mensaje} onChange={set('mensaje')} required />
      <input tabIndex={-1} autoComplete="off" aria-hidden="true" value={f.web} onChange={set('web')}
        style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }} />
      {error && <div style={{ color: '#C0392B', fontSize: 13.5 }}>{error}</div>}
      <div className="lp-form-acciones" style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <button type="submit" disabled={estado === 'enviando'} style={{ ...btnPrimary, opacity: estado === 'enviando' ? 0.6 : 1 }}>
          {estado === 'enviando' ? 'Enviando…' : 'Enviar consulta'}
        </button>
        <span style={{ fontSize: 13, color: COLOR.muted }}>
          O escríbenos a <a href={`mailto:${CONTACTO.email}`} style={{ color: COLOR.blue2 }}>{CONTACTO.email}</a>
        </span>
      </div>
    </form>
  );
};

// Consulta de estado desde la portada. Es lo primero que busca el cliente de
// un estudio que llega a tabularis.cl con el código que le dio su abogado, y
// antes tenía que encontrar un enlace chico en el menú. Acá escribe el código y
// el RUT y cae directo en /estado con la consulta ya hecha.
const ConsultaEstado: React.FC = () => {
  const [codigo, setCodigo] = useState('');
  const [rut, setRut] = useState('');
  // Van por sessionStorage y no por la URL: el RUT es dato personal y en la
  // dirección quedaría en el historial, en los registros de CloudFront y en
  // cualquier enlace que el cliente reenvíe.
  const ir = (e: React.FormEvent) => {
    e.preventDefault();
    try { sessionStorage.setItem(CONSULTA_PENDIENTE, JSON.stringify({ codigo: codigo.trim().toUpperCase(), rut: rut.trim() })); } catch { /* modo privado: llega a /estado y lo escribe de nuevo */ }
    window.location.href = '/estado';
  };
  const campo: React.CSSProperties = {
    ...input, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff',
    fontSize: 15, padding: '12px 14px',
  };
  return (
    <section id="estado" style={{ maxWidth: 1120, margin: '0 auto', padding: '0 6vw 3vw' }}>
      <div className="lp-estado-caja" style={{ background: COLOR.navy, borderRadius: 18, padding: 'clamp(24px, 4vw, 40px)', color: '#fff', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 28, alignItems: 'center', boxShadow: '0 16px 40px rgba(13,27,53,0.22)' }}>
        <div>
          <div style={{ fontSize: 12.5, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8FB0E6' }}>¿Eres cliente de un estudio?</div>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.5rem, 3.2vw, 2.2rem)', margin: '8px 0 10px', lineHeight: 1.15 }}>Consulta el estado de tu caso</h2>
          <p style={{ color: '#C3D2EA', fontSize: 15, lineHeight: 1.6, margin: 0 }}>
            Tu abogado te dio un <strong style={{ color: '#fff' }}>código</strong>. Escríbelo junto a tu RUT y ve en qué etapa va tu caso,
            tus audiencias y tus cuotas, a cualquier hora y sin llamar.
          </p>
          <p style={{ color: '#8FB0E6', fontSize: 13, lineHeight: 1.6, margin: '12px 0 0' }}>
            ¿Tienes un estudio? Cada caso que creas trae su código: tus clientes se informan solos y tú te enfocas en litigar.
          </p>
        </div>
        <form className="lp-estado-form" onSubmit={ir} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 14, padding: '22px 22px 20px', display: 'grid', gap: 12 }}>
          <div>
            <div style={{ fontSize: 12.5, color: '#B9C6DE', marginBottom: 5 }}>Código del caso</div>
            <input style={{ ...campo, fontFamily: 'monospace', letterSpacing: '0.12em', textTransform: 'uppercase' }} value={codigo} onChange={(e) => setCodigo(e.target.value.toUpperCase())} placeholder="SVA-7K2M9" required />
          </div>
          <div>
            <div style={{ fontSize: 12.5, color: '#B9C6DE', marginBottom: 5 }}>Tu RUT</div>
            <input style={campo} value={rut} onChange={(e) => setRut(e.target.value)} placeholder="12.345.678-9" required />
          </div>
          <button type="submit" style={{ ...btnPrimary, background: '#fff', color: COLOR.navy, padding: '13px 20px', fontSize: 15.5, marginTop: 4 }}>Consultar mi estado →</button>
          <div style={{ fontSize: 12, color: '#B9C6DE', textAlign: 'center' }}>Solo tú, con tu RUT, puedes ver tu caso.</div>
        </form>
      </div>
    </section>
  );
};

// Quiénes somos, misión y visión. Es la sección de confianza: quien compara
// software para su estudio quiere saber si hay una empresa real detrás, con
// dirección y personas que contestan. Nada de acá es aspiracional: Tabularis
// nació como la herramienta interna de un estudio jurídico chileno y sigue
// siendo la que ese estudio usa todos los días.
const QuienesSomos: React.FC = () => (
  <section id="nosotros" style={{ padding: '3vw 6vw 3vw', maxWidth: 1120, margin: '0 auto' }}>
    <div style={{ textAlign: 'center', marginBottom: 28 }}>
      <div style={{ fontSize: 12.5, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLOR.blue2 }}>Quiénes somos</div>
      <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.5rem, 3vw, 2.2rem)', color: COLOR.navy, margin: '8px 0 12px' }}>Nacimos dentro de un estudio jurídico</h2>
      <p style={{ color: COLOR.muted, fontSize: 15.5, lineHeight: 1.65, maxWidth: 720, margin: '0 auto' }}>
        Antes de ser un producto, Tabularis fue la herramienta con la que un estudio chileno ordenó sus propios casos, su cobranza y su equipo.
        Hoy la usan estudios de distintas ciudades y sigue creciendo con lo que ellos piden: cada función salió de una necesidad real
        en el escritorio de un abogado.
      </p>
    </div>

    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 18 }}>
      <div className="lp-ficha" style={{ border: `1px solid ${COLOR.line}`, borderRadius: 16, padding: '26px 24px', background: '#F8FAFD' }}>
        <div style={{ fontSize: 26 }}>🎯</div>
        <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 20, fontWeight: 600, color: COLOR.navy, margin: '10px 0 8px' }}>Misión</div>
        <p style={{ color: COLOR.muted, fontSize: 14.5, lineHeight: 1.65, margin: 0 }}>
          Que cualquier estudio jurídico de Chile, del tamaño que sea, trabaje con el orden y la tranquilidad de una gran firma:
          casos, plazos, pagos y equipo bajo control, y clientes informados sin tener que llamar.
        </p>
      </div>
      <div className="lp-ficha" style={{ border: `1px solid ${COLOR.line}`, borderRadius: 16, padding: '26px 24px', background: '#F8FAFD' }}>
        <div style={{ fontSize: 26 }}>🔭</div>
        <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 20, fontWeight: 600, color: COLOR.navy, margin: '10px 0 8px' }}>Visión</div>
        <p style={{ color: COLOR.muted, fontSize: 14.5, lineHeight: 1.65, margin: 0 }}>
          Ser la plataforma con la que los estudios jurídicos de Chile gestionan su trabajo y se relacionan con sus clientes,
          con los datos guardados en el país y protegidos por la ley chilena.
        </p>
      </div>
      <div className="lp-ficha" style={{ border: `1px solid ${COLOR.line}`, borderRadius: 16, padding: '26px 24px', background: '#F8FAFD' }}>
        <div style={{ fontSize: 26 }}>🤝</div>
        <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 20, fontWeight: 600, color: COLOR.navy, margin: '10px 0 8px' }}>Cómo trabajamos</div>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, color: COLOR.muted, fontSize: 14.5, lineHeight: 1.85 }}>
          <li>✓ Hecho por gente que litiga, para gente que litiga</li>
          <li>✓ Sin letra chica: todo incluido, pagas según cuántos abogados son</li>
          <li>✓ Soporte de personas, por WhatsApp y correo</li>
          <li>✓ Tus datos son tuyos: los descargas cuando quieras</li>
        </ul>
      </div>
    </div>

    {/* Dirección y contacto directo. Una oficina con calle y número es la señal
        de confianza más simple que hay: dice que hay a quién ir a buscar. */}
    <div style={{ marginTop: 18, border: `1px solid ${COLOR.line}`, borderRadius: 16, padding: '20px 24px', display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', background: '#fff' }}>
      <div style={{ display: 'flex', gap: 14, alignItems: 'center', flex: '1 1 280px' }}>
        <div style={{ fontSize: 26 }}>📍</div>
        <div>
          <div style={{ fontWeight: 600, color: COLOR.navy, fontSize: 15 }}>Tabularis SpA · {CONTACTO.ciudad}</div>
          <div style={{ color: COLOR.muted, fontSize: 14 }}>{CONTACTO.direccion}</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <a href={CONTACTO.whatsappUrl} target="_blank" rel="noreferrer" style={{ ...btnWhatsApp }}>
          <WhatsAppIconSmall size={16} /> {CONTACTO.whatsapp}
        </a>
        <a href={`mailto:${CONTACTO.email}`} style={{ ...btnGhost, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>{CONTACTO.email}</a>
      </div>
    </div>
  </section>
);

// Botón flotante de WhatsApp. Es el canal por el que un abogado pregunta antes
// de registrarse, y tiene que estar a la vista en cualquier punto de la página,
// no solo al final del todo en el formulario.
export const WhatsAppFlotante: React.FC = () => (
  <a
    className="lp-wa"
    href={CONTACTO.whatsappUrl} target="_blank" rel="noreferrer"
    aria-label={`Escríbenos por WhatsApp al ${CONTACTO.whatsapp}`} title="Escríbenos por WhatsApp"
    style={{
      position: 'fixed', right: 18, bottom: 18, zIndex: 50, width: 56, height: 56, borderRadius: '50%',
      background: COLOR.wa, display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: '0 8px 24px rgba(37,211,102,0.45)', textDecoration: 'none',
    }}
  >
    <WhatsAppIcon size={30} />
  </a>
);

/** El foro vive en el dominio propio: CloudFront manda /foro* a la API, que es
 *  quien arma esas páginas. Que esté acá y no en api.tabularis.cl es lo que
 *  hace que lo que gane en buscadores le sume a tabularis.cl. */
const FORO_URL = '/foro';

const LandingApp: React.FC = () => {
  const [modal, setModal] = useState<null | 'registro' | 'ingresar'>(null);
  // La demo con voz. Es aparte de `modal` porque desde ella se puede saltar
  // al registro: se cierra una y se abre la otra.
  const [video, setVideo] = useState(false);
  // Ya bajó del hero: en el teléfono la franja "Consulta tu estado" de la
  // barra se esconde para que la cabecera fija ocupe una sola fila. El
  // cliente que la busca la tiene arriba de todo y en su sección más abajo.
  useEffect(() => { registrarVisita('portada'); }, []);
  const [bajo, setBajo] = useState(false);
  useEffect(() => {
    const ver = () => setBajo(window.scrollY > 320);
    ver();
    window.addEventListener('scroll', ver, { passive: true });
    return () => window.removeEventListener('scroll', ver);
  }, []);
  // Con cuántos abogados se abre el registro: desde la calculadora llega el
  // número que la persona ya eligió; desde cualquier otro botón, uno.
  const [abogadosRegistro, setAbogadosRegistro] = useState(1);
  const [productoRegistro, setProductoRegistro] = useState<Producto>('tabularis');
  const abrirRegistro = (abogados = 1, producto: Producto = 'tabularis') => {
    registrarVisita('portada', 'clic_prueba');
    setAbogadosRegistro(abogados); setProductoRegistro(producto === 'ambos' ? 'tabularis' : producto); setModal('registro');
  };
  // El foro manda acá con ?ir=foro (&t=<hilo>) cuando alguien quiere responder.
  // Se abre el modal de ingreso solo, con el hilo a cuestas, y se limpia la
  // dirección para que recargar la página no vuelva a abrirlo.
  const [destinoForo, setDestinoForo] = useState<string | undefined>();
  // Registro a medias que vuelve por el enlace del correo del código.
  const [retomarRegistro, setRetomarRegistro] = useState<RegistroPendiente | null>(null);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('registro') === 'codigo' && p.get('sub') && p.get('email')) {
      const producto = p.get('producto');
      const abogados = Number(p.get('abogados'));
      setRetomarRegistro({
        estudio: p.get('estudio') || '', subdominio: p.get('sub') || '', nombre: p.get('nombre') || '', email: p.get('email') || '',
        codigo: /^\d{6}$/.test(p.get('codigo') || '') ? (p.get('codigo') as string) : undefined,
        abogados: abogados >= 1 ? abogados : undefined,
        producto: producto === 'tabularis' || producto === 'ambos' || producto === 'chatbularis' ? producto : undefined,
      });
      setModal('registro');
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    // Enlace de un aliado: se guarda el código y se limpia la dirección, sin
    // abrir nada. Quien llega así quiere mirar primero.
    const ref = p.get('ref');
    if (ref) guardarReferido(ref.trim().toUpperCase());
    // Enlace directo a crear la cuenta (tabularis.cl/?registro=chatbularis o
    // =tabularis; =ambos queda por los enlaces viejos). Es para mandarlo
    // por correo o WhatsApp a quien ya decidió probar: abre el registro con
    // el producto elegido, que igual se puede cambiar en el formulario. Se
    // combina con ?ref= para que el aliado siga contando.
    const registro = p.get('registro');
    if (registro === 'chatbularis' || registro === 'tabularis' || registro === 'ambos') {
      abrirRegistro(1, registro);
    }
    if (ref || registro) window.history.replaceState({}, '', window.location.pathname);
    if (p.get('ir') !== 'foro') return;
    setDestinoForo(p.get('t') || undefined);
    setModal('ingresar');
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  return (
    <div className="lp-root" style={{ fontFamily: 'Inter, sans-serif', color: COLOR.navy, background: '#fff', minHeight: '100vh' }}>
      {/* NAV. Los estilos van en línea; las clases lp-* existen para que
          LandingApp.css los reacomode en el celular. */}
      <FranjaPrecios />
      <nav className={`lp-nav${bajo ? ' lp-nav-bajo' : ''}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 6vw', borderBottom: `1px solid ${COLOR.line}`, position: 'sticky', top: 0, background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(6px)', zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: COLOR.blue }}>
          <LogoLockup height={32} />
        </div>
        <div className="lp-nav-acciones" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <a className="lp-nav-txt" href="#chatbularis" style={{ color: COLOR.muted, fontSize: 13.5, textDecoration: 'none', marginRight: 4 }}>Chatbularis IA</a>
          <a className="lp-nav-txt" href="#nosotros" style={{ color: COLOR.muted, fontSize: 13.5, textDecoration: 'none', marginRight: 4 }}>Quiénes somos</a>
          <a className="lp-nav-txt" href="#contacto" style={{ color: COLOR.muted, fontSize: 13.5, textDecoration: 'none', marginRight: 4 }}>Contacto</a>
          {/* La consulta de estado va como botón y no como enlace de texto: es
              a lo que viene el cliente de un estudio, que no va a registrarse
              ni a ingresar, y tiene que encontrarlo sin leer el menú. El foro
              se fue al pie: es para abogados que ya nos conocen. */}
          <a className="lp-nav-estado" href="/estado" style={{ ...btnGhost, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6, borderColor: COLOR.blue2 }}>🔎 Consulta tu estado</a>
          <button className="lp-nav-ingresar" onClick={() => setModal('ingresar')} style={btnGhost}>Ingresar</button>
          <button className="lp-nav-prueba" onClick={() => abrirRegistro()} style={btnPrimary}>Probar <span className="lp-nav-14">14 días </span>gratis</button>
        </div>
      </nav>

      {/* HERO. Dos columnas: el mensaje y, al lado, el panel en marcha. La
          prueba gratis sin tarjeta es lo primero que se lee, porque es lo
          primero que un abogado quiere saber antes de dejar su correo. */}
      <header className="lp-hero" style={{ padding: '4vw 6vw 3vw', maxWidth: 1160, margin: '0 auto' }}>
        <div className="lp-hero-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.05fr)', gap: 'clamp(28px, 4vw, 56px)', alignItems: 'center' }}>
          <div className="lp-hero-txt">
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, color: '#0F6B3C', background: '#E6F6EC', border: '1px solid #BFE6CC', borderRadius: 20, padding: '6px 14px', marginBottom: 18 }}>
              <span aria-hidden="true">✓</span> 14 días gratis · sin tarjeta
            </div>
            <h1 className="lp-hero-h1" style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(2rem, 4.2vw, 3.1rem)', lineHeight: 1.1, margin: 0, color: COLOR.navy }}>
              Tu estudio jurídico, ordenado y bajo control
            </h1>
            <p className="lp-hero-p" style={{ fontSize: 'clamp(1rem, 1.6vw, 1.15rem)', color: COLOR.muted, maxWidth: 560, margin: '18px 0 0', lineHeight: 1.6 }}>
              Casos, plazos, pagos y equipo en un solo lugar. Redacta <strong style={{ color: COLOR.navy }}>evaluaciones con IA</strong>,
              sube <strong style={{ color: COLOR.navy }}>documentos y fotos como PDF listo para tribunales</strong>, y tus clientes
              consultan su caso solos.
            </p>
            <div className="lp-hero-cta" style={{ display: 'flex', gap: 12, marginTop: 26, flexWrap: 'wrap' }}>
              <button onClick={() => abrirRegistro()} style={{ ...btnPrimary, padding: '14px 28px', fontSize: 16 }}>Probar 14 días gratis →</button>
              <button onClick={() => setVideo(true)} style={{ ...btnGhost, padding: '14px 22px', fontSize: 16, display: 'inline-flex', alignItems: 'center', gap: 8 }}>▶ Ver demo · {DEMO.duracion}</button>
            </div>
            <div style={{ fontSize: 13.5, color: COLOR.muted, marginTop: 14, lineHeight: 1.6 }}>
              Sin tarjeta · Sin instalar · Después, si te gusta, pagas con <strong style={{ color: COLOR.navy }}>débito, crédito o transferencia</strong>
            </div>
            <div style={{ fontSize: 13.5, color: COLOR.blue2, marginTop: 10 }}>
              ¿Eres cliente de un estudio? <a href="#estado" style={{ color: COLOR.blue2, fontWeight: 600 }}>Consulta el estado de tu caso ↓</a>
            </div>
          </div>
          <DemoVideo onVer={() => setVideo(true)} />
        </div>
        {/* Trust chips */}
        <div className="lp-chips" style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginTop: 34 }}>
          {['🇨🇱 Hecho en Chile · para abogados', '⚖️ Protegidos por la Ley 21.719', '🔒 Cifrados en AWS Sudamérica', '🏢 Aislado por estudio'].map((c) => (
            <span key={c} style={{ fontSize: 12.5, color: COLOR.muted, background: '#F5F8FC', border: `1px solid ${COLOR.line}`, borderRadius: 8, padding: '6px 12px' }}>{c}</span>
          ))}
        </div>
      </header>

      {/* VIDEOS: las cápsulas de venta (ver CarruselCapsulas). Si no hay
          catálogo publicado, no aparece nada. */}
      <CarruselCapsulas onRegistro={() => abrirRegistro()} />

      {/* DOS HERRAMIENTAS: panel y Chatbularis, para elegir camino. */}
      <DosHerramientas onRegistro={(producto) => abrirRegistro(1, producto)} />

      {/* ASÍ FUNCIONA: pruebas, decides, pagas. Antes que las funciones. */}
      <ComoFunciona onRegistro={() => abrirRegistro()} />

      {/* BENEFICIOS */}
      <section id="beneficios" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 18, padding: '3vw 6vw 3vw', maxWidth: 1120, margin: '0 auto' }}>
        {[
          ['📁', 'Casos y procesos', 'Estados, plazos, audiencias y etapas por disciplina, siempre a la vista.'],
          ['✨', 'Evaluaciones con IA', 'Anota rápido y la IA redacta una evaluación profesional lista para tu cliente.'],
          ['📸', 'Documentos y foto→PDF', 'Sube archivos o toma fotos que se compilan en un PDF liviano (≤5 MB), listo para las plataformas del Estado.'],
          ['👥', 'Tu equipo, con roles', 'Abogados, captadores y procuradores; cada quien ve solo lo que le corresponde.'],
          ['💳', 'Pagos y flujo de caja', 'Honorarios, cuotas y cobranza por caso, con el flujo de caja del estudio.'],
          ['🔒', 'Protección legal chilena', 'Tabularis está constituida en Chile, así que tus datos quedan sujetos a la Ley 21.719 estés donde estés. Aislamiento total por estudio y cifrado en reposo.'],
        ].map(([ic, t, d]) => (
          <div key={t} className="lp-beneficio lp-ficha" style={{ border: `1px solid ${COLOR.line}`, borderRadius: 14, padding: '22px 20px', background: '#F8FAFD' }}>
            <div style={{ fontSize: 24 }}>{ic}</div>
            <div style={{ fontWeight: 600, margin: '10px 0 6px', fontSize: 16.5 }}>{t}</div>
            <div style={{ color: COLOR.muted, fontSize: 13.5, lineHeight: 1.55 }}>{d}</div>
          </div>
        ))}
      </section>

      {/* CHATBULARIS: después de las funciones del panel, con su propio bloque. */}
      <ChatbularisSeccion onRegistro={() => abrirRegistro(1, 'chatbularis')} />

      {/* QUIÉNES SOMOS: antes de los precios, porque quien va a pagar quiere
          saber primero a quién. */}
      <QuienesSomos />

      {/* PLANES, con los medios de pago dentro de la calculadora. */}
      <Planes onRegistro={abrirRegistro} />
      <section style={{ padding: '0 6vw 3vw', maxWidth: 1160, margin: '0 auto' }}>
        <p style={{ textAlign: 'center', color: COLOR.muted, marginTop: 0, fontSize: 13.5 }}>
          ¿Estudio grande (30+ abogados)? <button onClick={() => abrirRegistro()} style={{ ...linkBtn }}>Plan Empresa a medida</button>.
        </p>
      </section>

      {/* PREGUNTAS FRECUENTES: justo después del precio, que es donde nacen. */}
      <Faq />

      {/* CTA FINAL */}
      <section style={{ textAlign: 'center', padding: '2vw 6vw 4vw', maxWidth: 720, margin: '0 auto' }}>
        <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.5rem, 3vw, 2.2rem)', color: COLOR.navy, margin: '0 0 10px' }}>Pon tu estudio en orden hoy</h2>
        <p style={{ color: COLOR.muted, fontSize: 15.5, margin: '0 0 22px', lineHeight: 1.6 }}>Crea tu estudio en un minuto y pruébalo 14 días gratis, sin tarjeta. Si te gusta, pagas con débito, crédito o transferencia.</p>
        <button onClick={() => abrirRegistro()} style={{ ...btnPrimary, padding: '15px 32px', fontSize: 16.5 }}>Probar 14 días gratis →</button>
      </section>

      {/* CONSULTA DE ESTADO: para el cliente de un estudio. Va acá abajo y no
          bajo el hero para no cortar el recorrido del abogado; el botón de la
          barra y el enlace del hero lo traen directo. */}
      <ConsultaEstado />

      {/* CONTACTO */}
      <section id="contacto" style={{ padding: '1vw 6vw 5vw', maxWidth: 820, margin: '0 auto' }}>
        <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.4rem, 3vw, 2rem)', color: COLOR.navy, margin: '0 0 8px', textAlign: 'center' }}>¿Tienes dudas antes de partir?</h2>
        <p style={{ color: COLOR.muted, fontSize: 15, textAlign: 'center', margin: '0 0 24px' }}>Escríbenos y te respondemos con calma. Sin compromiso.</p>
        {/* WhatsApp primero: es como un abogado pregunta hoy, y responde más
            rápido que un formulario. El formulario queda para quien prefiere
            dejar la consulta escrita. */}
        <div className="lp-contacto-btns" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 18 }}>
          <a href={CONTACTO.whatsappUrl} target="_blank" rel="noreferrer" style={{ ...btnWhatsApp, padding: '12px 22px', fontSize: 15 }}>
            <WhatsAppIconSmall size={18} /> WhatsApp {CONTACTO.whatsapp}
          </a>
          <a href={`mailto:${CONTACTO.email}`} style={{ ...btnGhost, padding: '12px 22px', fontSize: 15, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>✉️&nbsp; {CONTACTO.email}</a>
        </div>
        <Contacto />
        <p style={{ textAlign: 'center', color: COLOR.muted, fontSize: 13.5, marginTop: 16 }}>
          📍 {CONTACTO.direccion}, {CONTACTO.ciudad}
        </p>
      </section>

      {/* FOOTER */}
      <footer className="lp-footer" style={{ borderTop: `1px solid ${COLOR.line}`, padding: '32px 6vw', textAlign: 'center', color: COLOR.muted, fontSize: 13 }}>
        <div style={{ marginBottom: 10 }}><LogoLockup height={26} /></div>
        <div>Gestión legal para estudios jurídicos de Chile · tabularis.cl</div>
        <div style={{ marginTop: 6 }}>
          Tabularis SpA · RUT 78.486.429-4 · {CONTACTO.direccion}, {CONTACTO.ciudad}
        </div>
        <div style={{ marginTop: 6 }}>
          <a href={CONTACTO.whatsappUrl} target="_blank" rel="noreferrer" style={{ color: COLOR.blue2, textDecoration: 'none' }}>WhatsApp {CONTACTO.whatsapp}</a>
          {' · '}
          <a href={`mailto:${CONTACTO.email}`} style={{ color: COLOR.blue2, textDecoration: 'none' }}>{CONTACTO.email}</a>
        </div>
        <div style={{ marginTop: 10 }}>
          <a href="/estado" style={{ ...linkBtn, textDecoration: 'underline' }}>Consulta tu estado</a>
          {' · '}
          <button onClick={() => setModal('ingresar')} style={{ ...linkBtn }}>Ingresar al panel</button>
          {' · '}
          <button onClick={() => abrirRegistro()} style={{ ...linkBtn }}>Registrar estudio</button>
          {' · '}
          {/* /foro no lo sirve este sitio: CloudFront lo reenvía a la API, que
              arma esas páginas en el servidor para que las lea un buscador. */}
          <a href={FORO_URL} style={{ ...linkBtn, textDecoration: 'underline' }}>Foro de abogados</a>
          {' · '}
          <a href="/legal" style={{ ...linkBtn, textDecoration: 'underline' }}>Términos y Privacidad</a>
        </div>
        <div style={{ marginTop: 14, fontSize: 12 }}>© {new Date().getFullYear()} Tabularis SpA. Todos los derechos reservados.</div>
      </footer>

      <WhatsAppFlotante />

      {video && <VideoModal onClose={() => setVideo(false)} onEmpezar={() => { setVideo(false); abrirRegistro(); }} />}
      {modal === 'registro' && <RegistroModal onClose={() => { setModal(null); setRetomarRegistro(null); }} abogadosInicial={abogadosRegistro} productoInicial={productoRegistro} retomar={retomarRegistro} />}
      {modal === 'ingresar' && <IngresarModal onClose={() => setModal(null)} destinoForo={destinoForo} />}
    </div>
  );
};

// ── Modal: registrar estudio ────────────────────────────────────────
/* Registro en tres pasos: datos → código del correo → contraseña.
   El estudio se crea al final, cuando el correo ya está comprobado. Antes se
   creaba primero y se verificaba después, así que un tipeo en la dirección
   dejaba un estudio fantasma ocupando un subdominio para siempre, con un dueño
   que no podía entrar ni recuperar su contraseña.
   Todo ocurre en la misma pantalla: no hay que irse a la bandeja, abrir un
   enlace y volver. */
/** Un registro a medias: se pidió el código y falta escribirlo. Viene del
 *  enlace del correo (con el código incluido) o del borrador que queda en el
 *  navegador al pedir el código, para que cerrar la ventana no lo pierda. */
export type RegistroPendiente = {
  estudio: string; subdominio: string; nombre: string; email: string;
  codigo?: string; abogados?: number; producto?: Producto;
};
const REGISTRO_PENDIENTE = 'tabularis_registro_pendiente';
const REGISTRO_PENDIENTE_MIN = 15; // lo que dura el código
function guardarPendiente(r: RegistroPendiente) {
  try { localStorage.setItem(REGISTRO_PENDIENTE, JSON.stringify({ ...r, hasta: Date.now() + REGISTRO_PENDIENTE_MIN * 60000 })); } catch { /* modo privado */ }
}
function olvidarPendiente() { try { localStorage.removeItem(REGISTRO_PENDIENTE); } catch { /* modo privado */ } }
function pendienteGuardado(): RegistroPendiente | null {
  try {
    const raw = localStorage.getItem(REGISTRO_PENDIENTE);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (typeof d?.hasta !== 'number' || d.hasta < Date.now() || !d.subdominio || !d.email) { olvidarPendiente(); return null; }
    const { hasta, ...r } = d;
    return r as RegistroPendiente;
  } catch { return null; }
}

export const RegistroModal: React.FC<{ onClose: () => void; abogadosInicial?: number; productoInicial?: Producto; retomar?: RegistroPendiente | null }> = ({ onClose, abogadosInicial = 1, productoInicial = 'tabularis', retomar }) => {
  // Se retoma lo que venga del enlace del correo; si no, el borrador del
  // navegador; si no, se parte de cero.
  const inicial = retomar ?? pendienteGuardado();
  const [paso, setPaso] = useState<'datos' | 'codigo' | 'clave' | 'listo'>(inicial ? (inicial.codigo ? 'clave' : 'codigo') : 'datos');
  const [estudio, setEstudio] = useState(inicial?.estudio ?? '');
  const [subdominio, setSubdominio] = useState(inicial?.subdominio ?? '');
  // Cuántos abogados son. Fija los cupos y el precio del estudio desde el
  // primer día, así lo que se cobre después es lo que de verdad usan. Se
  // guarda el tope del plan (2 abogados es el plan "hasta 3").
  const [abogados, setAbogados] = useState<number>(planPara(Math.max(1, inicial?.abogados ?? abogadosInicial)).abogados);
  // Qué contrata: el panel (con Chatbularis prepago) o solo Chatbularis. Lo
  // elige acá porque decide qué ve al entrar y si hay mensualidad al terminar
  // la prueba. "ambos", de un registro a medias viejo, es el panel.
  const [producto, setProducto] = useState<Producto>(() => {
    const p = inicial?.producto ?? productoInicial;
    return p === 'ambos' ? 'tabularis' : p;
  });
  const conPanel = producto !== 'chatbularis';
  const precioMes = cobroTotal(abogados, 'mensual', producto).mensualClp;
  // Código de referido: parte con el que trajo el enlace, si lo hubo, y se
  // comprueba contra el servidor al escribirlo para decir "Recomendado por X"
  // antes de registrarse. Un código que no existe se avisa acá y no después.
  const [referido, setReferido] = useState(referidoGuardado);
  const [aliado, setAliado] = useState<{ ok: boolean; nombre?: string } | null>(null);
  useEffect(() => {
    const c = referido.trim();
    if (c.length < 3) { setAliado(null); return; }
    let vivo = true;
    const t = setTimeout(() => {
      api<{ ok: boolean; nombre?: string }>(`/auth/referido/${encodeURIComponent(c)}`, { auth: false })
        .then((r) => { if (vivo) setAliado(r); })
        .catch(() => { if (vivo) setAliado(null); });
    }, 350);
    return () => { vivo = false; clearTimeout(t); };
  }, [referido]);
  const [nombre, setNombre] = useState(inicial?.nombre ?? '');
  const [email, setEmail] = useState(inicial?.email ?? '');
  const [codigo, setCodigo] = useState(inicial?.codigo ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState(inicial && !retomar && !inicial.codigo ? 'Retomamos donde quedaste.' : '');
  const [loading, setLoading] = useState(false);
  const [subEditado, setSubEditado] = useState(false);

  const onEstudio = (v: string) => {
    setEstudio(v);
    if (!subEditado) setSubdominio(sanitizarSub(v));
  };

  // Paso 1 → pedir el código. No crea nada todavía.
  const pedirCodigo = async (e?: React.FormEvent): Promise<boolean> => {
    e?.preventDefault();
    setError(''); setAviso('');
    if (subdominio.length < 3) { setError('La dirección debe tener al menos 3 caracteres.'); return false; }
    setLoading(true);
    try {
      await api('/auth/registro/codigo', { method: 'POST', auth: false, body: { estudio, subdominio, nombre, email, abogados, producto } });
      // Queda un borrador: si cierra la ventana, al volver a "Crear estudio"
      // retoma en el paso del código en vez de empezar de cero.
      guardarPendiente({ estudio, subdominio, nombre, email, abogados, producto });
      setPaso('codigo');
      return true;
    } catch (err: any) {
      setError(err?.message || 'No pudimos enviar el código. Revisa el correo e inténtalo de nuevo.');
      return false;
    } finally { setLoading(false); }
  };

  // Paso 2 → el código no se valida contra el servidor por separado: se guarda
  // y viaja junto con la contraseña en el paso final, así el estudio se crea en
  // una sola operación y no queda nada a medio hacer si abandona ahora.
  const aceptarCodigo = (e: React.FormEvent) => {
    e.preventDefault();
    if (codigo.length !== 6) { setError('El código son 6 dígitos.'); return; }
    setError(''); setPaso('clave');
  };

  // Paso 3 → crear el estudio de verdad.
  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('La contraseña debe tener al menos 8 caracteres.'); return; }
    setLoading(true);
    try {
      await api('/auth/register', {
        method: 'POST', auth: false,
        body: { estudio, subdominio, codigo, abogados, producto, referido: referido.trim() || undefined, admin: { nombre, email, password } },
      });
      olvidarPendiente();
      setPaso('listo');
    } catch (err: any) {
      const datos = err?.datos;
      // Si el problema es el código, se vuelve a ese paso en vez de dejarlo
      // atascado en una pantalla donde no puede corregir lo que falla.
      if (datos?.motivoCodigo) {
        setCodigo(''); setPaso('codigo');
        if (datos.motivoCodigo === 'vencido') {
          // pedirCodigo limpia error y aviso al empezar: el mensaje va después.
          if (await pedirCodigo()) setAviso('El código venció. Te mandamos uno nuevo.');
        } else {
          setError('El código no coincide. Revísalo e inténtalo de nuevo.');
        }
      } else if (datos?.motivoReferido) {
        // Vuelve al primer paso, donde está el campo, con el mensaje puesto.
        setPaso('datos');
        setError(err?.message || 'El código de referido no es válido.');
      } else {
        setError(err?.status === 409 ? 'Esa dirección ya está en uso, prueba otra.' : (err?.message || 'No se pudo crear el estudio.'));
      }
    } finally { setLoading(false); }
  };

  const titulos = { datos: 'Registrar estudio', codigo: 'Confirma tu correo', clave: 'Crea tu contraseña', listo: 'Estudio registrado' };

  return (
    <Overlay onClose={onClose} titulo={titulos[paso]}>
      {paso === 'listo' ? (
        <div style={{ textAlign: 'center', padding: '10px 4px 6px' }}>
          <div style={{ fontSize: 34, marginBottom: 12 }}>✅</div>
          <div style={{ fontWeight: 600, fontSize: 17, marginBottom: 8 }}>{conPanel ? 'Tu estudio ya está activo' : 'Tu Chatbularis ya está activo'}</div>
          <p style={{ color: COLOR.muted, fontSize: 14.5, lineHeight: 1.6, margin: '0 0 18px' }}>
            {conPanel
              ? <>Tus 14 días de prueba empiezan ahora, con {clp(CHATBULARIS.bienvenidaClp)} de saldo de Chatbularis de regalo. </>
              // Solo Chatbularis no tiene prueba que venza ni mensualidad:
              // parte con el saldo de regalo y carga cuando quiera.
              : <>Tienes {clp(CHATBULARIS.bienvenidaClp)} de saldo de regalo; cuando quieras más, cargas desde el mismo Chatbularis. </>}
            Entra con <strong style={{ color: COLOR.navy }}>{email}</strong> y la contraseña que acabas de crear; te mandamos un correo con el enlace por si lo necesitas después.
          </p>
          <button type="button" onClick={() => {
            try { localStorage.setItem(ULTIMO_ESTUDIO, subdominio); } catch { /* modo privado */ }
            // Va a la pantalla de ingreso con el correo ya puesto: la del
            // panel, o la de Chatbularis si es lo único que contrató.
            const q = new URLSearchParams({ email });
            window.location.href = urlEstudio(subdominio) + (conPanel ? '/?' : '/asistente?') + q.toString();
          }} style={{ ...btnPrimary, width: '100%' }}>{conPanel ? 'Entrar a mi estudio →' : 'Abrir Chatbularis →'}</button>
        </div>
      ) : paso === 'datos' ? (
        <form onSubmit={pedirCodigo}>
          <div style={{ background: '#EAF1FB', border: `1px solid ${COLOR.line}`, borderRadius: 8, padding: '9px 12px', fontSize: 12.5, color: COLOR.blue, marginBottom: 14, lineHeight: 1.5 }}>⚡ {conPanel
            ? 'Tu estudio queda activo al instante: 14 días de prueba desde hoy, sin tarjeta y sin esperar a nadie.'
            : `Tu cuenta queda activa al instante, con ${clp(CHATBULARIS.bienvenidaClp)} de saldo de regalo, sin tarjeta y sin esperar a nadie.`}</div>
          <Campo label="Nombre del estudio"><input style={input} value={estudio} onChange={(e) => onEstudio(e.target.value)} placeholder="Estudio Jurídico Pérez" required /></Campo>
          <Campo label="Dirección (subdominio)">
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input style={{ ...input, flex: 1 }} value={subdominio} onChange={(e) => { setSubEditado(true); setSubdominio(sanitizarSub(e.target.value)); }} placeholder="estudioperez" required />
              <span style={{ color: COLOR.muted, fontSize: 13 }}>.tabularis.cl</span>
            </div>
          </Campo>
          <Campo label="¿Qué necesitas?">
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {PRODUCTOS.map((pr) => (
                <button key={pr.id} type="button" onClick={() => setProducto(pr.id)} style={{
                  flex: '1 1 40%', padding: '8px 6px', borderRadius: 9, fontSize: 12.5, cursor: 'pointer', lineHeight: 1.25,
                  border: `1px solid ${producto === pr.id ? COLOR.blue2 : COLOR.line}`,
                  background: producto === pr.id ? '#EAF1FB' : '#fff', color: producto === pr.id ? COLOR.blue2 : COLOR.navy, fontWeight: producto === pr.id ? 700 : 400,
                }}>{pr.etiqueta}</button>
              ))}
            </div>
          </Campo>
          {conPanel && (
            <Campo label="¿Cuántos abogados son?">
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {PLANES_PANEL.map((pl) => (
                  <button key={pl.abogados} type="button" onClick={() => setAbogados(pl.abogados)} style={{
                    flex: '1 1 28%', padding: '8px 6px', borderRadius: 9, fontSize: 12.5, cursor: 'pointer', lineHeight: 1.25,
                    border: `1px solid ${abogados === pl.abogados ? COLOR.blue2 : COLOR.line}`,
                    background: abogados === pl.abogados ? '#EAF1FB' : '#fff', color: abogados === pl.abogados ? COLOR.blue2 : COLOR.navy, fontWeight: abogados === pl.abogados ? 700 : 400,
                  }}>
                    {pl.etiqueta}
                    <div style={{ fontSize: 11, fontWeight: 400 }}>{clp(pl.precio)} al mes</div>
                  </button>
                ))}
              </div>
              <div style={{ fontSize: 12, color: COLOR.muted, marginTop: 6, lineHeight: 1.4 }}>
                Incluye {planPara(abogados).procuradores} procuradores; los captadores no se cobran. ¿Más de {MAX_ABOGADOS_PUBLICO} abogados? Escríbenos. Cambias de plan cuando quieras desde Configuración.
              </div>
            </Campo>
          )}
          <div style={{ fontSize: 12.5, color: COLOR.muted, marginTop: -4, marginBottom: 12, lineHeight: 1.5 }}>
            {conPanel
              ? <>Después de la prueba: <strong style={{ color: COLOR.navy }}>{clp(precioMes)}</strong> al mes por el panel, sin permanencia. Chatbularis va aparte, prepago: pagas solo lo que usas.</>
              : <>Sin mensualidad: pagas solo lo que usas, ≈ {clp(CHATBULARIS.redactar.tipicoClp)} por consulta para redactar.</>}
          </div>
          <Campo label="Código de referido (opcional)">
            <input className="lp-referido" style={{ ...input, textTransform: 'uppercase' }} value={referido} onChange={(e) => setReferido(e.target.value.toUpperCase())} placeholder="Código de quien te recomendó" maxLength={16} />
            {aliado?.ok && <div style={{ fontSize: 12.5, color: '#1B5E20', marginTop: 5 }}>✓ Recomendado por <strong>{aliado.nombre}</strong>.</div>}
            {aliado && !aliado.ok && referido.trim().length >= 3 && <div style={{ fontSize: 12.5, color: '#8A2A2A', marginTop: 5 }}>Ese código no existe. Revísalo o déjalo en blanco.</div>}
          </Campo>
          <Campo label="Tu nombre"><input style={input} value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Nombre y apellido" required /></Campo>
          <Campo label="Tu correo"><input style={input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@correo.cl" required /></Campo>
          <div style={{ fontSize: 12.5, color: COLOR.muted, marginTop: -4, marginBottom: 10, lineHeight: 1.5 }}>
            Te vamos a enviar un código a esa dirección para confirmarla. Es el correo con el que vas a entrar.
          </div>
          {error && <div style={errorBox}>{error}</div>}
          <button type="submit" disabled={loading} style={{ ...btnPrimary, width: '100%', padding: '12px', marginTop: 8 }}>{loading ? 'Enviando código…' : 'Enviarme el código →'}</button>
          <div style={{ fontSize: 12, color: COLOR.muted, textAlign: 'center', marginTop: 10 }}>Quedas como administrador. Luego invitas a tu equipo.<br />Al crear tu estudio aceptas los <a href="/legal" style={{ color: COLOR.blue }}>Términos y la Política de Privacidad</a>.</div>
        </form>
      ) : paso === 'codigo' ? (
        <form onSubmit={aceptarCodigo}>
          <p style={{ fontSize: 14.5, lineHeight: 1.6, marginTop: 0 }}>
            Te enviamos un código de 6 dígitos a <strong style={{ color: COLOR.navy }}>{email}</strong>.
            Escríbelo acá para seguir. Vence en 15 minutos.
          </p>
          <input
            style={{ ...input, fontSize: 28, letterSpacing: '0.35em', textAlign: 'center', fontFamily: 'monospace', padding: '14px 12px' }}
            value={codigo} onChange={(e) => { setError(''); setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6)); }}
            placeholder="000000" inputMode="numeric" autoComplete="one-time-code" autoFocus required
          />
          {aviso && <div style={{ background: '#EAF6EE', color: '#1B5E20', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginTop: 10 }}>{aviso}</div>}
          {error && <div style={errorBox}>{error}</div>}
          <button type="submit" disabled={codigo.length !== 6} style={{ ...btnPrimary, width: '100%', padding: '12px', marginTop: 12, opacity: codigo.length === 6 ? 1 : 0.5 }}>Aceptar código →</button>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, fontSize: 12.5 }}>
            <button type="button" onClick={() => { setPaso('datos'); setCodigo(''); setError(''); setAviso(''); olvidarPendiente(); }} style={linkBtn}>← Cambiar el correo</button>
            <button type="button" disabled={loading} onClick={async () => { if (await pedirCodigo()) setAviso('Te reenviamos el código.'); }} style={linkBtn}>
              {loading ? 'Enviando…' : 'No me llegó, reenviar'}
            </button>
          </div>
          <div style={{ fontSize: 12, color: COLOR.muted, textAlign: 'center', marginTop: 12, lineHeight: 1.5 }}>
            Todavía no creamos nada. Si cierras esta ventana, vuelves acá con el botón del correo o apretando "Crear estudio" de nuevo.
          </div>
        </form>
      ) : (
        <form onSubmit={crear}>
          <p style={{ fontSize: 14.5, lineHeight: 1.6, marginTop: 0 }}>
            Correo confirmado. Solo falta tu contraseña para entrar a <strong style={{ color: COLOR.navy }}>{subdominio}.tabularis.cl</strong>.
          </p>
          {retomar && (
            <div style={{ background: '#EAF6EE', color: '#1B5E20', borderRadius: 8, padding: '9px 12px', fontSize: 13, marginBottom: 12, lineHeight: 1.5 }}>
              Retomamos tu registro de <strong>{estudio}</strong> con el enlace del correo ({email}).
            </div>
          )}
          <Campo label="Contraseña"><ClaveInput style={input} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mínimo 8 caracteres" autoFocus required colorOjo={COLOR.muted} /></Campo>
          {error && <div style={errorBox}>{error}</div>}
          <button type="submit" disabled={loading} style={{ ...btnPrimary, width: '100%', padding: '12px', marginTop: 8 }}>{loading ? 'Creando…' : 'Crear mi estudio →'}</button>
        </form>
      )}
    </Overlay>
  );
};

/** Dónde se recuerda el estudio de quien ya entró una vez desde el dominio
 *  raíz. No es una sesión ni sirve para entrar: es solo el subdominio, para no
 *  preguntarlo cada vez. Las sesiones viven en el subdominio del estudio y no
 *  se comparten con esta página, que es pública. */
const ULTIMO_ESTUDIO = 'tabularis_ultimo_estudio';

// Código de referido que trae el enlace de un aliado (tabularis.cl/?ref=CODIGO).
// Se guarda 90 días en el navegador —el plazo con que trabajan los programas
// de afiliados de software— para que quien mira hoy y se registra en dos
// semanas siga contando para quien lo trajo. Es solo un valor por defecto del
// formulario: lo que manda es lo que el estudio deja escrito al registrarse.
const REFERIDO = 'tabularis_referido';
const REFERIDO_DIAS = 90;
function guardarReferido(codigo: string) {
  try { localStorage.setItem(REFERIDO, JSON.stringify({ codigo, hasta: Date.now() + REFERIDO_DIAS * 86400000 })); } catch { /* modo privado */ }
}
function referidoGuardado(): string {
  try {
    const raw = localStorage.getItem(REFERIDO);
    if (!raw) return '';
    const d = JSON.parse(raw);
    if (typeof d?.codigo !== 'string' || typeof d?.hasta !== 'number' || d.hasta < Date.now()) return '';
    return d.codigo;
  } catch { return ''; }
}

// ── Modal: ingresar ─────────────────────────────────────────────────
// Correo y contraseña, nada más. Antes se preguntaba primero el estudio
// (su subdominio), y mucha gente no lo recordaba o no entendía qué se le
// pedía. Ahora el servidor busca en qué estudios sirve esa combinación: con
// uno se entra directo, con varios la persona elige a cuál.
//
// La sesión no se puede abrir desde acá: vive en el navegador por dirección y
// el panel está en el subdominio del estudio. El servidor entrega un pase de
// un solo uso que el panel canjea al llegar, así no se pide la clave dos veces.
//
// `destinoForo` es el hilo del foro al que hay que volver después de entrar.
// El foro es público y vive en el dominio raíz; sin llevar el destino a
// cuestas, quien lee un hilo y quiere responder tiene que buscarlo otra vez.
/** `soloChatbularis` es la página de Chatbularis: después de entrar se va al
 *  asistente y no al panel. */
type EstudioIngreso = { subdominio: string; nombre: string; pase: string };
export const IngresarModal: React.FC<{ onClose: () => void; destinoForo?: string; soloChatbularis?: boolean }> = ({ onClose, destinoForo, soloChatbularis }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [entrando, setEntrando] = useState(false);
  const [estudios, setEstudios] = useState<EstudioIngreso[] | null>(null);

  const irA = (e: EstudioIngreso) => {
    try { localStorage.setItem(ULTIMO_ESTUDIO, e.subdominio); } catch { /* modo privado */ }
    const q = new URLSearchParams({ pase: e.pase });
    if (destinoForo) q.set('foro', destinoForo);
    else if (soloChatbularis) q.set('ir', 'asistente');
    window.location.href = `${urlEstudio(e.subdominio)}/?${q}`;
  };

  const entrar = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (entrando) return;
    setError(''); setAviso(''); setEntrando(true);
    try {
      const r = await api<{ estudios: EstudioIngreso[] }>('/auth/ingreso', { method: 'POST', auth: false, body: { email: email.trim(), password } });
      if (r.estudios.length === 1) { irA(r.estudios[0]); return; }
      // El último estudio al que entró va primero: es casi siempre al que vuelve.
      let ultimo = '';
      try { ultimo = localStorage.getItem(ULTIMO_ESTUDIO) || ''; } catch { /* modo privado */ }
      setEstudios([...r.estudios].sort((a, b) => Number(b.subdominio === ultimo) - Number(a.subdominio === ultimo)));
      setEntrando(false);
    } catch (e: any) {
      if (e?.status === 400) setError('Revisa que el correo esté completo y bien escrito.');
      else if (e?.status === 401) setError('Correo o contraseña incorrectos. Si no la recuerdas, usa "¿Olvidaste tu contraseña?".');
      else if (e?.status === 429) setError('Demasiados intentos seguidos. Espera unos minutos y vuelve a probar.');
      else setError(e?.message || 'No pudimos ingresar. Revisa tu conexión e inténtalo de nuevo.');
      setEntrando(false);
    }
  };

  const olvido = async () => {
    setError(''); setAviso('');
    if (!email.trim()) { setError('Escribe tu correo arriba y te mandamos el enlace.'); return; }
    try { await api('/auth/forgot', { method: 'POST', auth: false, body: { email: email.trim() } }); } catch { /* la respuesta es la misma exista o no */ }
    setAviso('Si ese correo tiene cuenta, te mandamos un enlace para crear una contraseña nueva. Revisa también la carpeta de spam.');
  };

  const titulo = destinoForo ? 'Entra a tu estudio para responder' : soloChatbularis ? 'Ingresar a Chatbularis' : 'Ingresar';

  if (estudios) {
    return (
      <Overlay onClose={onClose} titulo="¿A cuál estudio entras?">
        <div style={{ fontSize: 13.5, color: COLOR.muted, lineHeight: 1.6, marginBottom: 14 }}>
          Tu correo tiene cuenta en más de un estudio. Elige uno; después puedes volver acá y entrar al otro.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {estudios.map((e) => (
            <button key={e.subdominio} type="button" onClick={() => irA(e)} style={{
              textAlign: 'left', padding: '14px 16px', borderRadius: 10, cursor: 'pointer', fontFamily: 'Inter, sans-serif',
              border: `1px solid ${COLOR.line}`, background: '#fff', color: COLOR.navy,
            }}>
              <div style={{ fontWeight: 600, fontSize: 15 }}>{e.nombre}</div>
              <div style={{ fontSize: 12.5, color: COLOR.muted, marginTop: 2 }}>{e.subdominio}.tabularis.cl</div>
            </button>
          ))}
        </div>
      </Overlay>
    );
  }

  return (
    <Overlay onClose={onClose} titulo={titulo}>
      <form onSubmit={entrar}>
        {destinoForo && (
          <div style={{ fontSize: 13, color: COLOR.muted, lineHeight: 1.6, marginBottom: 14 }}>
            Publicar en el foro es para abogados de estudios con Tabularis. Entra al tuyo y te dejamos en el hilo que estabas leyendo.
          </div>
        )}
        <Campo label="Correo">
          <input style={input} type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@correo.cl" autoFocus required />
        </Campo>
        <Campo label="Contraseña">
          <ClaveInput style={input} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required colorOjo={COLOR.muted} />
        </Campo>
        {error && <div style={errorBox}>{error}</div>}
        {aviso && <div style={{ fontSize: 13, color: COLOR.blue, lineHeight: 1.5, marginBottom: 10 }}>{aviso}</div>}
        <button type="submit" disabled={entrando} style={{ ...btnPrimary, width: '100%', padding: '12px', marginTop: 8, opacity: entrando ? 0.6 : 1 }}>
          {entrando ? 'Entrando…' : destinoForo ? 'Entrar y responder →' : 'Entrar →'}
        </button>
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <button type="button" onClick={olvido} style={linkBtn}>¿Olvidaste tu contraseña?</button>
        </div>
      </form>
    </Overlay>
  );
};

// ── UI helpers ──────────────────────────────────────────────────────
const Overlay: React.FC<{ onClose: () => void; titulo: string; children: React.ReactNode }> = ({ onClose, titulo, children }) => {
  // Mientras la ventana está abierta, la página de atrás no se desplaza: el
  // desplazamiento es de la ventana misma, que tiene alto máximo y su propia
  // barra. Así el formulario largo de registro nunca queda cortado arriba ni
  // abajo, ni en escritorio ni en el teléfono.
  useEffect(() => {
    const previo = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previo; };
  }, []);
  return (
    // El fondo NO cierra la ventana: un clic fuera a mitad del registro
    // botaba el formulario con el código recién llegado, y la persona quedaba
    // sin saber dónde escribirlo. Se cierra solo con la ✕.
    <div className="lp-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(13,27,53,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 16 }}>
      <div className="lp-modal" role="dialog" aria-modal="true" aria-label={titulo} style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 420, maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto', overscrollBehavior: 'contain', boxShadow: '0 20px 50px rgba(13,27,53,0.3)', boxSizing: 'border-box' }}>
        {/* La cabecera va pegada arriba al desplazar, así la ✕ siempre está a mano. */}
        <div className="lp-modal-cab" style={{ position: 'sticky', top: 0, zIndex: 1, background: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '22px 26px 12px', borderBottom: `1px solid ${COLOR.line}` }}>
          <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 20, fontWeight: 600, color: COLOR.navy }}>{titulo}</div>
          <button type="button" aria-label="Cerrar" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: COLOR.muted, cursor: 'pointer', padding: '4px 6px', lineHeight: 1 }}>✕</button>
        </div>
        <div className="lp-modal-cuerpo" style={{ padding: '16px 26px 28px' }}>
          {children}
        </div>
      </div>
    </div>
  );
};

const Campo: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ marginBottom: 14 }}>
    <div style={{ fontSize: 13, color: COLOR.muted, marginBottom: 5 }}>{label}</div>
    {children}
  </div>
);

const input: React.CSSProperties = { width: '100%', padding: '10px 12px', border: `1px solid ${COLOR.line}`, borderRadius: 9, fontSize: 14, fontFamily: 'Inter, sans-serif', color: COLOR.navy, boxSizing: 'border-box' };
export const btnPrimary: React.CSSProperties = { background: COLOR.blue, color: '#fff', border: 'none', borderRadius: 9, padding: '9px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer' };
export const btnGhost: React.CSSProperties = { background: 'transparent', color: COLOR.blue, border: `1px solid ${COLOR.line}`, borderRadius: 9, padding: '9px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer' };
export const btnWhatsApp: React.CSSProperties = { background: '#25D366', color: '#fff', border: 'none', borderRadius: 9, padding: '9px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 8 };
export const linkBtn: React.CSSProperties = { background: 'none', border: 'none', color: COLOR.blue, cursor: 'pointer', fontSize: 13, textDecoration: 'underline' };
const errorBox: React.CSSProperties = { background: '#FDEDED', color: '#8A2A2A', borderRadius: 8, padding: '8px 12px', fontSize: 13, margin: '4px 0 12px' };

export default LandingApp;
